import React from 'react'
import { useSelector } from 'react-redux'
import { injectIntl, FormattedMessage, FormattedNumber, InjectedIntlProps } from 'react-intl'
import cx from 'classnames'
import TitledContent from 'components/TitledContent'
import RowLoader from 'components/RowLoader/RowLoader'
import Icon from 'components/Icon'
import Income from 'components/Income'
import NoData from 'components/NoData/NoData'
import { SECTION_HOST, SECTION_LISTING } from 'containers/Rentals/specs'
import { selectIsLoadingSummary, selectRentalsSummary } from 'store/rentals'
import messages from './messages'
import styles from './RentalsSummary.scss'

interface RentalsSummaryProps extends InjectedIntlProps {
  mode?: string
}

interface SummaryData {
  isInitialized: boolean
  hosts: Record<string, number>
  listings: Record<string, number>
}

const SECTION_SEPARATOR = 'separator'
const sections = [SECTION_HOST, SECTION_SEPARATOR, SECTION_LISTING]

const fields = ['total', 'documentedIncome', 'reportedIncome', 'taxGap'] as const
type Field = (typeof fields)[number]

const [FIELD_TOTAL] = fields

const RentalsSummary: React.FC<RentalsSummaryProps> = ({ intl, mode = SECTION_HOST }) => {
  const isLoading = useSelector(selectIsLoadingSummary)
  const summary = useSelector(selectRentalsSummary) as SummaryData

  const renderContent = (section: string, field: Field) => {
    if (isLoading) return <RowLoader />

    // @ts-ignore
    const value = (summary[section] || {})[field] || 0

    if (!value || !Number.isInteger(value)) return <NoData />
    if (field === FIELD_TOTAL) return <FormattedNumber value={value} />

    return <Income value={value} useCurrencyConversion={true} />
  }

  return (
    <div className={cx(styles.container, styles[mode])}>
      {sections.map(section => {
        if (section === SECTION_SEPARATOR) {
          return <div key={section} className={styles.separator} />
        }

        const iconName = `${section}-${mode === section ? 'active' : 'inactive'}`

        return (
          <section
            key={section}
            className={cx(styles.section, styles[section], {
              [styles.active]: section === mode,
            })}
          >
            <div className={styles.sectionLogo}>
              <Icon path='rentals' name={iconName} size='huge' />
              <h3 className={styles.sectionHeader}>
                {/* @ts-ignore */}
                <FormattedMessage {...messages.headers[section]} />
              </h3>
            </div>
            {fields.map(field => (
              <TitledContent
                key={field}
                // @ts-ignore
                title={intl.formatMessage(messages.fields[field], {
                  type: section,
                })}
                className={styles.field}
                titleClassName={styles.fieldTitle}
                contentClassName={cx(styles.fieldContent, styles[field])}
              >
                {renderContent(section, field)}
              </TitledContent>
            ))}
          </section>
        )
      })}
    </div>
  )
}

export default injectIntl(RentalsSummary)
